import React, { useState } from "react";
import {
  Modal,
  Box,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const SummaryModal = ({
  open,
  onClose,
  loading,
  completed,
  onGenerate,
  onAccept,
  onReject,
  summaryResponse,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  // =========================================================
  // HELPER FUNCTION
  // Safely convert any value into displayable text
  // =========================================================
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return JSON.stringify(item, null, 2);
          }

          return String(item);
        })
        .join("\n");
    }

    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  // =========================================================
  // RENDER ARRAY / OBJECT VALUES
  // =========================================================
  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    // Array
    if (Array.isArray(value)) {
      return (
        <Box
          component="ul"
          sx={{
            marginTop: "8px",
            marginBottom: "8px",
            paddingLeft: "25px",
          }}
        >
          {value.map((item, index) => (
            <li key={index}>
              {typeof item === "object" &&
              item !== null ? (
                <Box
                  component="pre"
                  sx={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    fontFamily:
                      "Arial, sans-serif",
                    fontSize: "14px",
                  }}
                >
                  {JSON.stringify(item, null, 2)}
                </Box>
              ) : (
                String(item)
              )}
            </li>
          ))}
        </Box>
      );
    }

    // Object
    if (typeof value === "object") {
      return (
        <Box
          sx={{
            padding: "10px 15px",
            backgroundColor: "#f8f8f8",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
          }}
        >
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily:
                "Arial, sans-serif",
              fontSize: "14px",
            }}
          >
            {JSON.stringify(value, null, 2)}
          </pre>
        </Box>
      );
    }

    return String(value);
  };

  // =========================================================
  // RENDER COMPLETE SUMMARY OBJECT
  // =========================================================
  const renderSummaryObject = (summary) => {
    if (!summary || typeof summary !== "object") {
      return null;
    }

    return (
      <Box
        sx={{
          textAlign: "left",
          fontFamily: "Arial, sans-serif",
          fontSize: "15px",
          lineHeight: 1.6,
          color: "#222",
        }}
      >
        {/* =================================================
            DOCUMENT TYPE
        ================================================== */}
        {summary.document_type && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginTop: 0,
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Document Type
            </h3>

            <Box>
              {renderValue(
                summary.document_type
              )}
            </Box>
          </Box>
        )}

        {/* =================================================
            PARTIES INVOLVED
        ================================================== */}
        {summary.parties_involved && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Parties Involved
            </h3>

            {renderValue(
              summary.parties_involved
            )}
          </Box>
        )}

        {/* =================================================
            PROPERTY DETAILS
        ================================================== */}
        {summary.property_details && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Property Details
            </h3>

            {renderValue(
              summary.property_details
            )}
          </Box>
        )}

        {/* =================================================
            KEY DATES
        ================================================== */}
        {summary.key_dates && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Key Dates
            </h3>

            {renderValue(
              summary.key_dates
            )}
          </Box>
        )}

        {/* =================================================
            LEGAL TERMS
        ================================================== */}
        {summary.legal_terms && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Legal Terms
            </h3>

            {renderValue(
              summary.legal_terms
            )}
          </Box>
        )}

        {/* =================================================
            SUMMARY
        ================================================== */}
        {summary.summary && (
          <Box sx={{ marginBottom: "25px" }}>
            <h3
              style={{
                marginBottom: "8px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              Summary
            </h3>

            <Box
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.7,
              }}
            >
              {typeof summary.summary ===
              "object"
                ? renderValue(summary.summary)
                : summary.summary}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // =========================================================
  // HANDLE PREVIEW
  // =========================================================
  const handlePreview = async () => {
    console.log(
      "Summary Response:",
      summaryResponse
    );

    setPreviewOpen(true);
    setPreviewText("");
    setPreviewError("");

    // =======================================================
    // CASE 1:
    // Backend directly returns summary object
    // =======================================================
    if (
      summaryResponse?.summary &&
      typeof summaryResponse.summary === "object"
    ) {
      return;
    }

    // =======================================================
    // CASE 2:
    // Backend directly returns summary string
    // =======================================================
    if (
      summaryResponse?.summary &&
      typeof summaryResponse.summary === "string"
    ) {
      setPreviewText(
        summaryResponse.summary
      );

      return;
    }

    // =======================================================
    // CASE 3:
    // Backend returns download_url / preview_url
    // =======================================================
    const previewUrl =
      summaryResponse?.download_url ||
      summaryResponse?.preview_url;

    if (!previewUrl) {
      setPreviewError(
        "Summary preview URL is not available."
      );

      return;
    }

    try {
      setPreviewLoading(true);

      console.log(
        "Loading summary from:",
        previewUrl
      );

      const response = await fetch(
        previewUrl
      );

      if (!response.ok) {
        throw new Error(
          `Failed to load summary. Status: ${response.status}`
        );
      }

      const text =
        await response.text();

      console.log(
        "Summary preview text:",
        text
      );

      setPreviewText(text);
    } catch (error) {
      console.error(
        "Summary preview failed:",
        error
      );

      setPreviewError(
        error?.message ||
          "Unable to preview summary."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // =========================================================
  // CLOSE PREVIEW
  // =========================================================
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewText("");
    setPreviewError("");
  };

  return (
    <>
      {/* =====================================================
          MAIN SUMMARY MODAL
      ====================================================== */}
      <Modal
        open={open}
        onClose={
          loading ? undefined : onClose
        }
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: 450,

            bgcolor:
              "background.paper",

            boxShadow: 24,

            borderRadius: 2,

            p: 4,

            textAlign: "center",
          }}
        >
          {/* TITLE */}
          <h2
            style={{
              marginTop: 0,
            }}
          >
            Summary
          </h2>

          {/* =================================================
              INITIAL STATE
          ================================================== */}
          {!loading && !completed && (
            <>
              <p>
                Generate a summary for this
                document?
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "center",
                  gap: "12px",
                  marginTop: "25px",
                }}
              >
                {/* GENERATE */}
                <Button
                  variant="contained"
                  onClick={onGenerate}
                >
                  Generate Summary
                </Button>

                {/* CANCEL */}
                <Button
                  variant="outlined"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {/* =================================================
              PROCESSING
          ================================================== */}
          {loading && (
            <div
              style={{
                display: "flex",
                flexDirection:
                  "column",
                alignItems:
                  "center",
                gap: "15px",
                padding:
                  "20px 0",
              }}
            >
              <CircularProgress />

              <p
                style={{
                  margin: 0,
                }}
              >
                Generating summary...
              </p>

              <p
                style={{
                  margin: 0,
                  fontSize:
                    "13px",
                  color:
                    "#666",
                }}
              >
                Please wait while
                the document is
                being processed.
              </p>
            </div>
          )}

          {/* =================================================
              COMPLETED
          ================================================== */}
          {!loading && completed && (
            <>
              <p>
                Summary generated
                successfully.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "center",
                  gap: "10px",
                  marginTop:
                    "25px",
                  flexWrap:
                    "wrap",
                }}
              >
                {/* PREVIEW */}
                <Button
                  variant="outlined"
                  onClick={
                    handlePreview
                  }
                  sx={{
                    borderRadius:
                      "20px",

                    minWidth:
                      "90px",

                    height:
                      "38px",

                    textTransform:
                      "none",
                  }}
                >
                  Preview
                </Button>

                {/* ACCEPT */}
                <Button
                  variant="contained"
                  onClick={onAccept}
                  sx={{
                    backgroundColor:
                      "#1976d2",

                    borderRadius:
                      "20px",

                    minWidth:
                      "90px",

                    height:
                      "38px",

                    textTransform:
                      "none",

                    "&:hover": {
                      backgroundColor:
                        "#1976d2",
                    },
                  }}
                >
                  Accept
                </Button>

                {/* REJECT */}
                <Button
                  variant="contained"
                  color="error"
                  onClick={onReject}
                  sx={{
                    borderRadius:
                      "20px",

                    minWidth:
                      "90px",

                    height:
                      "38px",

                    textTransform:
                      "none",

                    "&:hover": {
                      backgroundColor:
                        "#d32f2f",
                    },
                  }}
                >
                  Reject
                </Button>
              </div>
            </>
          )}
        </Box>
      </Modal>

      {/* =====================================================
          SUMMARY PREVIEW MODAL
      ====================================================== */}
      <Modal
        open={previewOpen}
        onClose={
          handleClosePreview
        }
      >
        <Box
          sx={{
            position: "absolute",

            top: "50%",

            left: "50%",

            transform:
              "translate(-50%, -50%)",

            width: "80%",

            maxWidth:
              "1200px",

            height: "80vh",

            bgcolor: "#ffffff",

            boxShadow: 24,

            borderRadius: 2,

            overflow: "hidden",

            display: "flex",

            flexDirection:
              "column",
          }}
        >
          {/* =================================================
              HEADER
          ================================================== */}
          <Box
            sx={{
              display: "flex",

              alignItems:
                "center",

              justifyContent:
                "space-between",

              padding:
                "12px 20px",

              borderBottom:
                "1px solid #ddd",

              backgroundColor:
                "#ffffff",
            }}
          >
            <h2
              style={{
                margin: 0,

                fontSize:
                  "20px",

                fontWeight:
                  500,
              }}
            >
              Summary Preview
            </h2>

            <IconButton
              onClick={
                handleClosePreview
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* =================================================
              CONTENT
          ================================================== */}
          <Box
            sx={{
              flex: 1,

              overflowY:
                "auto",

              padding:
                "25px",

              backgroundColor:
                "#ffffff",
            }}
          >
            {/* LOADING */}
            {previewLoading && (
              <Box
                sx={{
                  height:
                    "100%",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",

                  flexDirection:
                    "column",

                  gap: 2,
                }}
              >
                <CircularProgress />

                <span>
                  Loading
                  summary...
                </span>
              </Box>
            )}

            {/* ERROR */}
            {!previewLoading &&
              previewError && (
                <Box
                  sx={{
                    textAlign:
                      "center",

                    color:
                      "#666",

                    paddingTop:
                      "50px",
                  }}
                >
                  {previewError}
                </Box>
              )}

            {/* =================================================
                OBJECT SUMMARY
            ================================================== */}
            {!previewLoading &&
              !previewError &&
              summaryResponse?.summary &&
              typeof summaryResponse.summary ===
                "object" &&
              renderSummaryObject(
                summaryResponse.summary
              )}

            {/* =================================================
                STRING SUMMARY / DOWNLOADED TXT
            ================================================== */}
            {!previewLoading &&
              !previewError &&
              typeof summaryResponse?.summary !==
                "object" &&
              previewText && (
                <Box
                  component="pre"
                  sx={{
                    margin: 0,

                    whiteSpace:
                      "pre-wrap",

                    wordBreak:
                      "break-word",

                    textAlign:
                      "left",

                    fontFamily:
                      "Arial, sans-serif",

                    fontSize:
                      "15px",

                    lineHeight:
                      1.6,

                    color:
                      "#222",

                    backgroundColor:
                      "#ffffff",
                  }}
                >
                  {previewText}
                </Box>
              )}

            {/* =================================================
                NO PREVIEW
            ================================================== */}
            {!previewLoading &&
              !previewError &&
              !summaryResponse?.summary &&
              !previewText && (
                <Box
                  sx={{
                    textAlign:
                      "center",

                    color:
                      "#666",

                    paddingTop:
                      "50px",
                  }}
                >
                  Summary preview is
                  not available.
                </Box>
              )}
          </Box>

          {/* =================================================
              FOOTER
          ================================================== */}
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "flex-end",

              padding:
                "12px 20px",

              borderTop:
                "1px solid #ddd",

              backgroundColor:
                "#ffffff",
            }}
          >
            <Button
              variant="contained"
              onClick={
                handleClosePreview
              }
              sx={{
                textTransform:
                  "none",
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SummaryModal;